import { useEffect, useState, useMemo } from 'react'
import { shallowEqual } from 'react-redux'
import { useSelector } from 'APP/Hooks/redux'
import Analytics from 'APP/Services/Analytics'
import I18n from 'APP/I18n'
import {
  type ChannelUploadSnapshot,
  type ChannelUploadStatus,
  ChannelUploadStatusEnum,
  FileUploadStatusEnum,
} from 'APP/Store/History/types'
import { type SupportedFileWithMetadata } from 'APP/Converse/FilePreview/types'

interface UseIntakePhotoLogicParams {
  maximum_selection?: number
  files: SupportedFileWithMetadata[]
  hasPendingUpload: boolean
  channelUpload: ChannelUploadSnapshot | undefined
  channelUploadStatus: ChannelUploadStatus | undefined
  isTooLarge: boolean
  duplicateFileError?: string | null
}

const NETWORK_PROBLEM_CODES = ['NETWORK_ERROR', 'TIMEOUT_ERROR', 'CONNECTION_ERROR']
const OTHER_PROBLEM_CODES = ['SERVER_ERROR', 'CLIENT_ERROR', 'CANCEL_ERROR', 'UNKNOWN_ERROR']

const getUploadErrorMessage = (error?: string | null) => {
  if (error && NETWORK_PROBLEM_CODES.includes(error)) {
    return I18n.t('ImagePicker.errorAlert.network')
  }
  if (!error || OTHER_PROBLEM_CODES.includes(error)) {
    return I18n.t('ImagePicker.errorAlert.upload')
  }
  return error
}

export const useChannelUploadState = () => {
  return useSelector((state) => {
    const history = state.history
    const currentChannelId = history?.currentChannelId
    return {
      channelId: currentChannelId,
      channelUploadStatus: currentChannelId ? history?.fileUploads?.[currentChannelId] : undefined,
      channelUpload: currentChannelId ? history?.uploads?.[currentChannelId] : undefined,
    }
  }, shallowEqual)
}

export const useIntakePhotoLogic = ({
  maximum_selection = 1,
  files,
  hasPendingUpload,
  channelUpload,
  channelUploadStatus,
  isTooLarge,
  duplicateFileError,
}: UseIntakePhotoLogicParams) => {
  const [showUploadError, setShowUploadError] = useState(false)
  const [uploadErrorMessage, setUploadErrorMessage] = useState('')
  const [isValidating, setIsValidating] = useState(false)
  const maxUploadFiles = maximum_selection

  useEffect(() => {
    const shouldShowError =
      channelUploadStatus === ChannelUploadStatusEnum.ERROR && files.length === 0

    setShowUploadError((prevShowUploadError) => {
      if (shouldShowError && !prevShowUploadError) {
        Analytics.trackStructuredEvent('Chat Action', 'File upload error', 'upload_failed', '', 0)
      }
      return shouldShowError
    })
  }, [channelUploadStatus, files])

  useEffect(() => {
    const isProcessing =
      hasPendingUpload ||
      channelUpload?.status === FileUploadStatusEnum.UPLOADING ||
      channelUpload?.status === FileUploadStatusEnum.REMOVING_FILE

    if (isValidating && channelUploadStatus === ChannelUploadStatusEnum.DONE && !isProcessing) {
      setIsValidating(false)
    }
  }, [isValidating, channelUploadStatus, hasPendingUpload, channelUpload?.status])

  useEffect(() => {
    if (channelUploadStatus === ChannelUploadStatusEnum.ERROR) {
      setUploadErrorMessage(getUploadErrorMessage(channelUpload?.error))
      return
    }

    if (channelUploadStatus) {
      setUploadErrorMessage('')
    }
  }, [channelUploadStatus, channelUpload?.error])

  const errorMessage = useMemo(() => {
    if (duplicateFileError) return duplicateFileError
    if (isTooLarge) return I18n.t('ImagePicker.errorAlert.fileTooLarge')
    if (uploadErrorMessage) return uploadErrorMessage
    return ''
  }, [duplicateFileError, isTooLarge, uploadErrorMessage])

  const isProcessingUpload =
    hasPendingUpload ||
    channelUpload?.status === FileUploadStatusEnum.UPLOADING ||
    channelUpload?.status === FileUploadStatusEnum.REMOVING_FILE

  const hasAnyFile = files.length > 0
  const showUploadingPill = isProcessingUpload || isValidating

  return {
    showUploadError,
    setShowUploadError,
    isValidating,
    setIsValidating,
    maxUploadFiles,
    isProcessingUpload,
    hasAnyFile,
    showUploadingPill,
    errorMessage,
  }
}

export const createIntakePhotoSubmitHandler = (
  files: SupportedFileWithMetadata[],
  sendAnswer: (answer?: string[]) => void,
  setIsValidating: (val: boolean) => void
) => {
  return () => {
    const uploadedFiles = files.filter(
      (file) => file.uploadStatus === FileUploadStatusEnum.UPLOADED
    )
    setIsValidating(true)

    if (uploadedFiles.length > 0) {
      const fileIds = uploadedFiles.map((file) => file.fileId).filter(Boolean) as string[]
      sendAnswer(fileIds)
      return
    }

    sendAnswer()
  }
}
